import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Brain, PaperPlaneRight, Spinner, Clock, Trash } from '@phosphor-icons/react';

import { API_URL } from '../config';

const AICoachPage = () => {
  const { user, isPremium } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/ai-coach/sessions`, {
        withCredentials: true
      });
      setSessions(response.data);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };

  const loadSession = async (sid) => {
    try {
      const response = await axios.get(`${API_URL}/api/ai-coach/history?session_id=${sid}`, {
        withCredentials: true
      });
      const history = response.data.reverse();
      setMessages(history.map(h => ({
        role: h.role,
        content: h.content,
        timestamp: h.created_at
      })));
      setSessionId(sid);
      setShowSessions(false);
    } catch (error) {
      console.error('Error loading session:', error);
    }
  };

  const startNewSession = () => {
    setMessages([]);
    setSessionId(null);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage, timestamp: new Date().toISOString() }]);
    setLoading(true);

    try {
      const response = await axios.post(
        `${API_URL}/api/ai-coach/chat`,
        { message: userMessage, session_id: sessionId },
        { withCredentials: true }
      );

      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response,
        timestamp: new Date().toISOString()
      }]);
      setSessionId(response.data.session_id);
      fetchSessions();
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'I apologize, but I\'m having trouble responding right now. Please try again.',
        timestamp: new Date().toISOString(),
        error: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  const suggestedPrompts = [
    "What's happening in the markets today?",
    "Explain how compound interest works.",
    "Give me a quick summary of the latest tech news.",
    "Help me write a professional email.",
    "What are some good productivity tips?",
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-16 flex flex-col">
      <div className="flex-1 flex max-w-7xl mx-auto w-full">
        {/* Sessions Sidebar - Desktop */}
        <div className="hidden lg:block w-64 border-r border-white/10 p-4">
          <button
            onClick={startNewSession}
            className="w-full mb-4 py-3 bg-[#007AFF] text-white font-bold uppercase tracking-widest text-sm hover:bg-[#005bb5] transition-colors"
            data-testid="new-session-btn"
          >
            New Chat
          </button>
          <h3 className="text-xs text-[#8892B0] uppercase tracking-wider mb-3">Previous Sessions</h3>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {sessions.map((session) => (
              <button
                key={session.session_id}
                onClick={() => loadSession(session.session_id)}
                className={`w-full text-left p-3 text-sm transition-colors ${
                  sessionId === session.session_id
                    ? 'bg-white/10 text-white'
                    : 'text-[#8892B0] hover:bg-white/5 hover:text-white'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Clock size={14} />
                  <span className="truncate">
                    {new Date(session.last_message).toLocaleDateString()}
                  </span>
                </div>
                <span className="text-xs text-[#5C6B82]">{session.message_count} messages</span>
              </button>
            ))}
          </div>
        </div>

        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col">
          {/* Chat Header */}
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Brain size={28} weight="duotone" className="text-[#007AFF]" />
              <div>
                <h1 className="font-heading font-bold text-lg uppercase text-white" data-testid="ai-coach-title">
                  AI Assistant
                </h1>
                <p className="text-xs text-[#8892B0]">Powered by GPT-5.2</p>
              </div>
            </div>
            <button
              onClick={() => setShowSessions(!showSessions)}
              className="lg:hidden text-[#8892B0] hover:text-white"
            >
              <Clock size={24} />
            </button>
          </div>

          {/* Mobile Sessions Panel */}
          {showSessions && (
            <div className="lg:hidden border-b border-white/10 p-4 bg-[#131313]">
              <button
                onClick={startNewSession}
                className="w-full mb-3 py-2 bg-[#007AFF] text-white font-bold uppercase tracking-widest text-sm"
              >
                New Chat
              </button>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {sessions.slice(0, 5).map((session) => (
                  <button
                    key={session.session_id}
                    onClick={() => loadSession(session.session_id)}
                    className="w-full text-left p-2 text-sm text-[#8892B0] hover:bg-white/5"
                  >
                    {new Date(session.last_message).toLocaleDateString()} ({session.message_count} msgs)
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chat-messages">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-8">
                <Brain size={64} weight="duotone" className="text-[#007AFF] mb-6" />
                <h2 className="font-heading font-bold text-2xl uppercase text-white mb-3">
                  Your AI Assistant
                </h2>
                <p className="text-[#8892B0] max-w-md mb-8">
                  Ask me anything — general knowledge, writing help, brainstorming, or just a conversation.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-2xl">
                  {suggestedPrompts.map((prompt) => (
                    <button
                      key={prompt}
                      onClick={() => setInput(prompt)}
                      className="text-left p-4 bg-[#131313] border border-white/10 text-[#8892B0] text-sm hover:bg-[#1A1A1A] hover:text-white hover:border-white/20 transition-colors"
                      data-testid={`suggested-prompt-${prompt.slice(0, 20)}`}
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((message, index) => (
                <div
                  key={`${message.role}-${message.created_at || index}`}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] p-4 ${
                      message.role === 'user'
                        ? 'bg-[#007AFF] text-white'
                        : message.error
                        ? 'bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30]'
                        : 'bg-[#131313] border border-white/10 text-white'
                    }`}
                  >
                    {message.role === 'assistant' && !message.error && (
                      <div className="flex items-center gap-2 mb-2 text-[#007AFF]">
                        <Brain size={16} weight="duotone" />
                        <span className="text-xs font-bold uppercase tracking-wider">AI Coach</span>
                      </div>
                    )}
                    <p className={`whitespace-pre-wrap ${message.role === 'assistant' ? 'font-mono text-sm' : ''}`}>
                      {message.content}
                    </p>
                  </div>
                </div>
              ))
            )}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-[#131313] border border-white/10 p-4">
                  <div className="flex items-center gap-2 text-[#007AFF]">
                    <Spinner size={16} className="animate-spin" />
                    <span className="text-xs font-bold uppercase tracking-wider">Thinking...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={sendMessage} className="p-4 border-t border-white/10" data-testid="chat-input-form">
            <div className="flex gap-3">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask me anything..."
                className="flex-1 bg-[#131313] border border-white/20 text-white p-4 focus:border-[#007AFF] focus:outline-none placeholder-[#5C6B82]"
                disabled={loading}
                data-testid="ai-coach-input"
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="px-6 bg-[#007AFF] text-white font-bold hover:bg-[#005bb5] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="ai-coach-send"
              >
                <PaperPlaneRight size={24} weight="fill" />
              </button>
            </div>
            {!isPremium && (
              <p className="mt-2 text-xs text-[#5C6B82]">
                Free tier: Basic AI access. Upgrade to Premium for unlimited access and priority responses.
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default AICoachPage;
